import _ from 'lodash'
import moment from 'moment'
import Flatten from 'flatten-js'
import * as Collections from 'typescript-collections'

import { workflow_brick_size, workflow_brick_icon_size } from '@/utils.js'

// *****************************************************************************

export class ASet extends Collections.Set {}

export class FrozenSet extends Collections.Set {}

export class Dict extends Collections.Dictionary {}

export class FrozenDict extends Collections.Dictionary {}

export class OrderedDict extends Collections.BSTreeKV {}

export class FrozenOrderedDict extends Collections.BSTreeKV {}

class nat_class {}

const nat = new nat_class()

export { nat }

// *****************************************************************************

function json_sort_instance_to_type_priority(v) {
  if (_.isNull(v)) {
    return 0
  } else if (_.isBoolean(v)) {
    if (v) {
      return 2
    } else {
      return 1
    }
  } else if (_.isNumber(v)) {
    if (Number.isNaN(v)) {
      return 3
    } else if (v === Number.POSITIVE_INFINITY) {
      return 5
    } else if (v === Number.NEGATIVE_INFINITY) {
      return 4
    }
    return 6
  } else if (_.isString(v)) {
    return 7
  } else if (_.isArray(v)) {
    return 8
  }
  // Plain object
  return 9
}

function json_sort_lexicographic_compare(lhs, rhs) {
  let cur = 0
  for (;;) {
    let i = lhs[cur]
    let j = rhs[cur]
    if (i === undefined) {
      if (j === undefined) {
        return 0
      }
      return -1
    }
    if (j === undefined) {
      return 1
    }

    let res = json_sort_compare(i, j)
    if (res !== 0) {
      return res
    }

    cur += 1
  }
}

function json_sort_compare(lhs, rhs) {
  const lhs_category = json_sort_instance_to_type_priority(lhs)
  const rhs_category = json_sort_instance_to_type_priority(rhs)
  let res = lhs_category - rhs_category
  if (res !== 0 || lhs_category <= 5) {
    return res
  }
  if (lhs_category === 6) {
    return lhs - rhs
  } else if (lhs_category === 7) {
    return lhs < rhs ? -1 : lhs === rhs ? 0 : 1
  } else if (lhs_category === 8) {
    return json_sort_lexicographic_compare(lhs, rhs)
  } else {
    let nlhs = []
    lhs.forEach((key, value) => nlhs.push([key, value]))
    nlhs = json_sort(lhs)
    let nrhs = []
    rhs.forEach((key, value) => nrhs.push([key, value]))
    nrhs = json_sort(rhs)
    return json_sort_lexicographic_compare(nlhs, nrhs)
  }
}

function json_sort(v) {
  return v.sort(json_sort_compare)
}

export class SerializerType {
  constructor() {
    let special_type_dispatcher = {
      frozenset: this.visit_frozenset.bind(this),
      set: this.visit_set.bind(this),
      frozendict: this.visit_frozendict.bind(this),
      dict: this.visit_dict.bind(this),
      frozenordereddict: this.visit_frozenordereddict.bind(this),
      ordereddict: this.visit_ordereddict.bind(this),
      nat: this.visit_nat.bind(this),
    }
    FrozenSet.__constructor_name__ = 'frozenset'
    ASet.__constructor_name__ = 'set'
    FrozenDict.__constructor_name__ = 'frozendict'
    Dict.__constructor_name__ = 'dict'
    FrozenOrderedDict.__constructor_name__ = 'frozenordereddict'
    OrderedDict.__constructor_name__ = 'ordereddict'
    nat_class.__constructor_name__ = 'nat'
    this.special_type_dispatcher = special_type_dispatcher
    let struct_like_dispatcher = {}
    this.struct_like_dispatcher = struct_like_dispatcher
  }
  register_class(oname, klass) {
    const minifiedname = oname
    const fullname = _.snakeCase(oname)
    this.struct_like_dispatcher[minifiedname] = fullname
  }
  create_complex_object(stype, contents) {
    let d = {
      __type__: stype,
      contents: contents,
    }
    return d
  }
  visit_default(v) {
    let res = v
    if (_.isNumber(v)) {
      if (Number.isNaN(v)) {
        res = { __type__: 'NaN' }
      } else if (v === Number.POSITIVE_INFINITY) {
        res = { __type__: '+Infinity' }
      } else if (v === Number.NEGATIVE_INFINITY) {
        res = { __type__: '-Infinity' }
      }
    } else if (!(_.isNull(v) | _.isBoolean(v) | _.isString(v))) {
      throw new TypeError('Serializing this object is forbidden: ' + v)
    }
    return res
  }
  visit_nat(v) {
    const res = { __type__: 'NaN' }
    return res
  }
  visit_datetime(v) {
    const res = this.create_complex_object('datetime', moment(v).toISOString())
    return res
  }
  visit_timedelta(v) {
    const res = this.create_complex_object('timedelta', v.asSeconds())
    return res
  }
  visit_list(v) {
    const res = v.map(n => this.do_visit(n))
    return res
  }
  visit_frozenset(v) {
    let contents = []
    v.forEach(item => contents.push(this.do_visit(item)))
    contents = json_sort(contents)
    const res = this.create_complex_object('frozenset', contents)
    return res
  }
  visit_set(v) {
    let contents = []
    v.forEach(item => contents.push(this.do_visit(item)))
    contents = json_sort(contents)
    const res = this.create_complex_object('set', contents)
    return res
  }
  visit_frozendict(v) {
    let contents = []
    v.forEach((key, value) =>
      contents.push([this.do_visit(key), this.do_visit(value)])
    )
    contents = json_sort(contents)
    const res = this.create_complex_object('frozendict', contents)
    return res
  }
  visit_object(v) {
    // _.mapValues creates an object with the same keys as object
    // and values generated by the do_visit function
    const res = _.mapValues(v, n => this.do_visit(n))
    return res
  }
  visit_dict(v) {
    let contents = []
    v.forEach((key, value) =>
      contents.push([this.do_visit(key), this.do_visit(value)])
    )
    contents = json_sort(contents)
    const res = this.create_complex_object('dict', contents)
    return res
  }
  visit_ordereddict(v) {
    let contents = []
    v.forEach((key, value) =>
      contents.push([this.do_visit(key), this.do_visit(value)])
    )
    const res = this.create_complex_object('ordereddict', contents)
    return res
  }
  visit_frozenordereddict(v) {
    let contents = []
    v.forEach((key, value) =>
      contents.push([this.do_visit(key), this.do_visit(value)])
    )
    const res = this.create_complex_object('frozenordereddict', contents)
    return res
  }
  visit_struct_like(v, constructor_name) {
    const fullname = this.struct_like_dispatcher[constructor_name]
    let d = { __type__: fullname }
    for (let [k, val] of Object.entries(v)) {
      d[k] = this.do_visit(val)
    }
    return d
  }
  do_visit(v) {
    let constructor_name = null
    try {
      constructor_name = (v.constructor || {}).__constructor_name__ || null
    } catch (err) {}
    let res

    if (constructor_name in this.special_type_dispatcher) {
      res = this.special_type_dispatcher[constructor_name](v)
    } else if (constructor_name in this.struct_like_dispatcher) {
      res = this.visit_struct_like(v, constructor_name)
    } else if (_.isDate(v) | moment.isMoment(v)) {
      res = this.visit_datetime(v)
    } else if (moment.isDuration(v)) {
      res = this.visit_timedelta(v)
    } else if (_.isArray(v)) {
      res = this.visit_list(v)
    } else if (_.isPlainObject(v)) {
      res = this.visit_object(v)
    } else {
      res = this.visit_default(v)
    }
    return res
  }
  visit(v) {
    let res
    try {
      res = this.do_visit(v)
    } catch (err) {
      console.warn('Coud not serialize ', v)
      throw err
    }
    return res
  }
}

export let serializer = new SerializerType()

// *****************************************************************************

export class UnserializerType {
  constructor() {
    let dispatcher = {}
    dispatcher['NaN'] = this.unserialize_nan.bind(this)
    dispatcher['+Infinity'] = this.unserialize_posinf.bind(this)
    dispatcher['-Infinity'] = this.unserialize_neginf.bind(this)
    dispatcher['NaT'] = this.unserialize_nat.bind(this)
    dispatcher['datetime'] = this.unserialize_datetime.bind(this)
    dispatcher['timedelta'] = this.unserialize_timedelta.bind(this)
    dispatcher['bytearray'] = this.unserialize_bytearray.bind(this)
    dispatcher['frozenset'] = this.unserialize_frozenset.bind(this)
    dispatcher['set'] = this.unserialize_set.bind(this)
    dispatcher['frozendict'] = this.unserialize_frozendict.bind(this)
    dispatcher['dict'] = this.unserialize_dict.bind(this)
    dispatcher['frozenordereddict'] = this.unserialize_frozenordereddict.bind(
      this
    )
    dispatcher['ordereddict'] = this.unserialize_ordereddict.bind(this)
    this.dispatcher = dispatcher
  }
  register_class(oname, klass) {
    const fullname = _.snakeCase(oname)
    this.dispatcher[fullname] = v => this.unserialize_struct_like(klass, v)
  }
  visit_default(v) {
    if (_.isNull(v) | _.isBoolean(v) | _.isNumber(v) | _.isString(v)) {
      return v
    } else {
      throw new TypeError(
        'Unserializing this object is forbidden: ' + JSON.stringify(v)
      )
    }
  }
  unserialize_nan() {
    return Number.NaN
  }
  unserialize_posinf() {
    return Number.POSITIVE_INFINITY
  }
  unserialize_neginf() {
    return Number.NEGATIVE_INFINITY
  }
  unserialize_nat() {
    return nat
  }
  unserialize_datetime(v) {
    const res = moment.utc(v[0][1])
    return res
  }
  unserialize_timedelta(v) {
    const res = moment.duration(v[0][1], 'seconds')
    return res
  }
  unserialize_bytearray(v) {
    const res = btoa(v[0][1])
    return res
  }
  visit_list(v) {
    const res = v.map(n => this.do_visit(n))
    return res
  }
  unserialize_frozenset(v) {
    let res = new FrozenSet()
    let t = v[0][1]
    t.forEach(n => res.add(n))
    return res
  }
  unserialize_set(v) {
    let res = new Collections.Set()
    let t = v[0][1]
    t.forEach(n => res.add(n))
    return res
  }
  visit_object(v) {
    let res = _.mapValues(v, n => this.do_visit(n))
    if ('__type__' in res) {
      let t = res.__type__
      delete res['__type__']
      try {
        res = this.dispatcher[t](Object.entries(res))
      } catch (err) {
        console.warn('Error while fetching dispatcher for ', t, v)
        throw err
      }
    }
    return res
  }
  unserialize_frozendict(v) {
    const res = new FrozenDict()
    v[0][1].forEach(([k, v]) => res.setValue(k, v))
    return res
  }
  unserialize_dict(v) {
    const res = new Dict()
    v[0][1].forEach(([k, v]) => res.setValue(k, v))
    return res
  }
  unserialize_ordereddict(v) {
    const res = new OrderedDict()
    v[0][1].forEach(([k, v]) => res.setValue(k, v))
    return res
  }
  unserialize_frozenordereddict(v) {
    const res = new FrozenOrderedDict()
    v[0][1].forEach(([k, v]) => res.setValue(k, v))
    return res
  }
  unserialize_struct_like(constructor, v) {
    // StructLike objects are stored "as is" without the
    // "contents" key
    let object = new constructor()
    v.map(e => {
      object[e[0]] = e[1]
    })
    return object
  }
  do_visit(v) {
    let res
    if (_.isArray(v)) {
      res = this.visit_list(v)
    } else if (_.isPlainObject(v)) {
      res = this.visit_object(v)
    } else {
      res = this.visit_default(v)
    }
    return res
  }
  visit(v) {
    try {
      const res = this.do_visit(v)
      return res
    } catch (err) {
      console.warn('Could not unserialize ', v)
      throw err
    }
  }
}

export let unserializer = new UnserializerType()

// *****************************************************************************

class StructLike {
  toString() {
    let body = _.join(
      _.map(Object.getOwnPropertyNames(this).sort(), x => `${x}: ${this[x]}`),
      ', '
    )
    let res = this.constructor.__constructor_name__ + '(' + body + ')'
    return res
  }
}

// *****************************************************************************

function struct_like_registerer_decorator(name) {
  return function decorator(klass) {
    klass.__constructor_name__ = name
    serializer.register_class(name, klass)
    unserializer.register_class(name, klass)
    return klass
  }
}

@struct_like_registerer_decorator('PipeProxy')
export class PipeProxy extends StructLike {
  constructor() {
    super()
    this.nb = -1
  }
}

export class AutomlBase extends StructLike {
  constructor() {
    super()
    this.uid = -1
    this.creation_dt = Date.now()
  }
}

export class AutomlObjectModelBase extends AutomlBase {
  constructor() {
    super()
    this.is_deleted = false
    this.project_id = -1
  }
}

@struct_like_registerer_decorator('AutomlBrickStatus')
export class AutomlBrickStatus extends StructLike {
  constructor() {
    super()
    this.computations_failed = new Collections.Set()
    this.instantiations_failed = new Collections.Set()
    this.successfully_instantiated = new Collections.Set()
  }
}

export class AutomlBrickBase extends AutomlObjectModelBase {
  constructor() {
    super()
    this.description = ''
    this.short_description = ''
    this.position = { x: workflow_brick_size / 2, y: workflow_brick_size / 2 }
    this.last_modification_dt = Date.now()
  }
  get box() {
    let { x, y } = this.position
    return new Flatten.Box(
      x - workflow_brick_icon_size / 2,
      y - workflow_brick_icon_size / 2,
      x + workflow_brick_icon_size / 2,
      y + workflow_brick_icon_size / 2
    )
  }
  get large_box() {
    let { x, y } = this.position
    return new Flatten.Box(
      x - workflow_brick_size / 2,
      y - workflow_brick_size / 2,
      x + workflow_brick_size / 2,
      y + workflow_brick_size / 2
    )
  }
}

export class AutomlTableBase extends AutomlBrickBase {
  constructor() {
    super()
  }
}

// *****************************************************************************

@struct_like_registerer_decorator('AutomlFile')
export class AutomlFile extends AutomlTableBase {
  constructor() {
    super()
    this.brick_name = 'sourcefile'
    this.uploaded_rawfile_uid = -1
    this.generic_sourcefilepath = null
    this.active_folder_scanning_activated = true
    this.metavariables_dict_to_rawfile_uid = new Dict()
    this.csv_format_info = null
    this.coercion_info_delta = new CoercionInfoDelta()
    this.files_infos_entities = []
  }
}

// @struct_like_registerer_decorator('AutomlDatabase')
// export class AutomlDatabase extends AutomlTableBase {
//   constructor() {
//     super()
//     this.brick_name = 'database'
//     this.username = ''
//     this.password = ''
//     this.dsn = ''
//     this.encoding = 'UTF-8'
//     this.nencoding = 'UTF-8'
//     this.query_string = ''
//     this.parameter_code = ''
//     this.batch_size = 10000
//     this.availability_string = ''
//     this.rrule_check = null
//   }
// }

@struct_like_registerer_decorator('AutomlDatabase')
export class AutomlDatabase extends AutomlTableBase {
  constructor() {
    super()
    this.brick_name = 'database'

    this.username = ''
    this.password = ''
    this.dsn = ''

    this.is_new_database = false
    this.database_name = ''

    this.encoding = 'UTF-8'
    this.nencoding = 'UTF-8'
    this.query_string = ''
    this.parameter_code = ''
    this.batch_size = 10000
    this.availability_string = ''
    this.rrule_check = null
  }
}

@struct_like_registerer_decorator('AutomlApiInput')
export class AutomlApiInput extends AutomlBrickBase {
  constructor() {
    super()
    this.defined_manually = true
    this.endpoint = ''
    this.synchronous = false
    // this.manual_json = ''
    this.manual_json = encodeURI({
      prospects: { client_id: 1, last_contact_dt: '' },
      mailing: [
        {
          mailing_id: 0,
          client_id: '1',
          result: 0,
          job: 'director',
          last_contact_dt: '18/12/2016',
        },
        {
          mailing_id: 1,
          client_id: '3',
          result: 0,
          job: 'assistant',
          last_contact_dt: '25/09/2012',
        },
      ],
    })
    this.json_object_path = undefined
  }
}

// *****************************************************************************
// Unique parents tables

export class AutomlUniqueParentTable extends AutomlTableBase {
  constructor() {
    super()
    this.has_connector_problem = false
  }
}

@struct_like_registerer_decorator('AutomlAddIdColumn')
export class AutomlAddIdColumn extends AutomlUniqueParentTable {
  constructor() {
    super()
    this.brick_name = 'addid'
    this.id_colname = ''
    this.short_description = "Ajout de colonne d'identifiant"
    this.default_name = 'addid'
  }
}

@struct_like_registerer_decorator('AutomlAddConstColumn')
export class AutomlAddConstColumn extends AutomlUniqueParentTable {
  constructor() {
    super()
    this.const_colname = ''
    this.const_type = 'number'
    this.const_value = ''
  }
}

@struct_like_registerer_decorator('AutomlColumnsRenamer')
export class AutomlColumnsRenamer extends AutomlUniqueParentTable {
  constructor() {
    super()
    this.to_delete = []
    this.to_rename = []
    this.to_copy = []
  }
}

@struct_like_registerer_decorator('AutomlHistoryFeatureEngineeredTable')
export class AutomlHistoryFeatureEngineeredTable extends AutomlUniqueParentTable {
  constructor() {
    super()
    this.period_column = null
    this.separate_extend_table = true
    this.ref_is_range = true
    this.comp_is_range = true
    this.ref_suffix = ''
    this.comp_suffix = ''
    this.ref_values = [0, 0]
    this.comp_values = [0, 0]
    this.keep_nonhistorypart_table = false
    this.keep_historypart_table = false
  }
}

@struct_like_registerer_decorator('AutomlUnique')
export class AutomlUnique extends AutomlUniqueParentTable {
  constructor() {
    super()
    this.unicity_cols = []
    this.discriminating_cols = []
  }
}

@struct_like_registerer_decorator('AutomlFilter')
export class AutomlFilter extends AutomlUniqueParentTable {
  constructor() {
    super()
    this.filter_formula = ''
  }
}

@struct_like_registerer_decorator('AutomlApiTable')
export class AutomlApiTable extends AutomlUniqueParentTable {
  constructor() {
    super()
    // this.table_to_json = false
    // this.json_to_table = false
    this.input_coercion = []
    this.output_coercion = []
    this.chained_operations = []
  }
}

// *****************************************************************************

export class AutomlModelgeneratorBase extends AutomlUniqueParentTable {
  constructor() {
    super()
    this.classification_task = true
    this.target_column = null
    this.index_columns = []
    this.cv_columns = []
    this.scorer = null
    this.nb_folds = 3
    this.cv_seed = 0
    this.single_cv_asked_fold = 0
    this.nb_single_cv_iters = 4
    this.decision_colname = 'decision'
  }
}

@struct_like_registerer_decorator('AutomlGenericModelgenerator')
export class AutomlGenericModelgenerator extends AutomlModelgeneratorBase {
  constructor() {
    super()
    this.is_caruana = true
    this.model = 'small_gbdt'
    this.models = ['small_gbdt', 'logistic_regression']
    this.nb_caruana_iters = 5
    this.nb_full_cv_iters = 2
    this.compute_feature_importance = true
    this.boruta = true
    this.nb_boruta_iterations = 10
    this.boruta_perc = 80
    this.boruta_alpha = 0.01
    this.boruta_two_step = true
    this.automatic_nb_folds = true
    this.automatic_model_selection = true
  }
}

@struct_like_registerer_decorator('AutomlResult')
export class AutomlResult extends AutomlUniqueParentTable {
  constructor() {
    super()
    this.filename = 'default_result.csv'
    this.separator = ','
    this.choose_all_cols = true
    this.columns_wanted = []
    this.write_index = false
  }
}

@struct_like_registerer_decorator('AutomlApiOutput')
export class AutomlApiOutput extends AutomlUniqueParentTable {
  constructor() {
    super()
    // this.synchronous = false
    this.endpoint = ''
    // this.url = ''
  }
}

@struct_like_registerer_decorator('AutomlProbeColumns')
export class AutomlProbeColumns extends AutomlUniqueParentTable {
  constructor() {
    super()
    // this.filename = 'default_result.csv'
    // this.separator = ','
    // this.choose_all_cols = true
    // this.columns_wanted = []
    // this.write_index = false
  }
}

@struct_like_registerer_decorator('AutomlProbeDataset')
export class AutomlProbeDataset extends AutomlUniqueParentTable {
  constructor() {
    super()
  }
}

@struct_like_registerer_decorator('AutomlProbeModel')
export class AutomlProbeModel extends AutomlUniqueParentTable {
  constructor() {
    super()
  }
}

// *****************************************************************************

@struct_like_registerer_decorator('BasicTableInfoEntity')
export class BasicTableInfoEntity extends StructLike {
  constructor() {
    super()
    this.uid = -1
    this.version = '1.0.0'
  }
}

@struct_like_registerer_decorator('DispatchTableInfoEntity')
export class DispatchTableInfoEntity extends BasicTableInfoEntity {
  constructor() {
    super()
    this.metavariables_expressions = ''
  }
}

@struct_like_registerer_decorator('HstackTableInfoEntity')
export class HstackTableInfoEntity extends BasicTableInfoEntity {
  constructor() {
    super()
    this.extension_type = 'no_extension'
    this.name_extension = ''
  }
}

// @struct_like_registerer_decorator('MetavariablesDictFileInfoEntity')
// export class MetavariablesDictFileInfoEntity extends BasicTableInfoEntity {
//   constructor() {
//     super()
//     this.metavariables_dict_poid = -1
//     this.uploaded_rawfile_uid = -1
//     this.selected_path = ''
//     this.active_folder_scanning_activated = false
//   }
// }

@struct_like_registerer_decorator('FileInfosEntity')
export class FileInfosEntity extends StructLike {
  constructor() {
    super()
    this.name = ''
    this.uploaded_rawfile_uid = -1
    this.upload_category = 'new'
    this.generic_sourcefilepath = new GenericPath()
    this.active_folder_scanning_activated = false
    this.associated_expression = 'True'
    // this.associated_expression_contains_metavariables = false
    this.version = '1.0.0'
  }
}

// *****************************************************************************

// Multiple parents tables

@struct_like_registerer_decorator('AutomlDispatchTable')
export class AutomlDispatchTable extends AutomlTableBase {}

@struct_like_registerer_decorator('AutomlHstackTable')
export class AutomlHstackTable extends AutomlTableBase {}

// @struct_like_registerer_decorator('AutomlVstackTable')
// export class AutomlVstackTable extends AutomlTableBase {}

@struct_like_registerer_decorator('AutomlVstackTable')
export class AutomlVstackTable extends AutomlTableBase {
  constructor() {
    super()
    this.is_temporal = false
    this.temporal_metavariable_name = new GenericColumnName()
    this.start = 0
    this.stop = 0
  }
}

@struct_like_registerer_decorator('AutomlHistoryTable')
export class AutomlHistoryTable extends AutomlTableBase {
  constructor() {
    super()
    this.index_columns = []
    this.period_column = null
    this.start = 0
    this.stop = 0
  }
}

@struct_like_registerer_decorator('AutomlManualFeatureEngineeredTable')
export class AutomlManualFeatureEngineeredTable extends AutomlTableBase {
  constructor() {
    super()
    this.reference_table_name = ''
    this.created_columns = []
    this.creation_mode = 'independent'
  }
}

// *****************************************************************************

@struct_like_registerer_decorator('AutomlSingleRelation')
export class AutomlSingleRelation extends StructLike {
  constructor() {
    super()
    this.lhs_table_name = ''
    this.rhs_table_name = ''
    this.id_columns_lhs = []
    this.id_columns_rhs = []
    this.relation_type = -1
    this.version = '1.0.0'
  }
}

@struct_like_registerer_decorator('AutomlRelationDelta')
export class AutomlRelationDelta extends StructLike {
  constructor() {
    super()
    this.delta_mode = ''
    this.relation = new AutomlSingleRelation()
    this.version = '1.0.0'
  }
}

@struct_like_registerer_decorator('AutomlAggregation')
export class AutomlAggregation extends AutomlTableBase {
  constructor() {
    super()
    this.junction_table_name = ''
    this.relations = []
  }
}

// *****************************************************************************

@struct_like_registerer_decorator('AutomlFinish')
export class AutomlFinish extends AutomlTableBase {}

// *****************************************************************************

@struct_like_registerer_decorator('AutomlProjectSnapshot')
export class AutomlProjectSnapshot extends AutomlBase {
  constructor() {
    super()
    this.version = '1.0.0'
    this.is_deleted = false
    this.project_id = -1
    this.snapshot_hash = ''
    this.time_saved = Date.now()
    this.snapshot_name = ''
    this.nb_bricks = -1
    this.snapshot = undefined
  }
}

// *****************************************************************************

@struct_like_registerer_decorator('AutomlProjectRights')
export class AutomlProjectRights extends AutomlBase {
  constructor() {
    super()
    this.version = '1.0.0'
    this.is_deleted = false
    this.project_id = -1
    this.user_id = -1
    this.project_group_id = -1
  }
}

// *****************************************************************************

@struct_like_registerer_decorator('AutomlRawfile')
export class AutomlRawfile extends AutomlObjectModelBase {
  constructor() {
    super()
    this.name = ''
    this.origin_category = ''
    this.origin_path = ''
    this.mime_type = ''
    this.size = -1
    this.last_modified_dt = Date.now()
  }
}

// *****************************************************************************

@struct_like_registerer_decorator('AutomlProject')
export default class AutomlProject extends AutomlObjectModelBase {
  constructor() {
    super()
    this.name = ''
    this.description = ''
    this.models_ids = []
    // this.metavariables_list = []
    this.metavariables_types = []
    this.metavariables_dicts = []
    this.metavariables_dicts_definition_code = ''
    this.metavariables_dicts_definition_code_stdoutput = ''
    this.metavariables_dicts_definition_code_error = ''
    this.is_paused = false
    this.is_in_production = false
  }
}

// *****************************************************************************

@struct_like_registerer_decorator('AutomlConnector')
export class AutomlConnector extends AutomlObjectModelBase {
  constructor() {
    super()
    this.brick_name = 'connector'
    this.description = ''
    this.from_brick_uid = -1
    this.from_port = 'mr'
    this.to_brick_uid = -1
    this.to_port = 'ml'
  }
}

// *****************************************************************************

@struct_like_registerer_decorator('AutomlLog')
export class AutomlLog extends AutomlBase {
  constructor() {
    super()
    this.project_id = -1
    this.entry_nb = -1
    this.content = ''
  }
}

// *****************************************************************************

@struct_like_registerer_decorator('AutomlUser')
export class AutomlUser extends AutomlBase {
  constructor() {
    super()
    this.version = '1.0.0'
    this.is_deleted = false
    this.username = ''
    this.api_key = ''
    this.password_hash = ''
    this.global_group_id = -1
  }
}

// // *****************************************************************************

// @struct_like_registerer_decorator('AutomlDatabaseElement')
// export class AutomlDatabaseElement extends AutomlBase {
//   constructor() {
//     super()
//     this.version = '1.0.0'
//     this.is_deleted = false
//     this.username = ''
//     this.password = ''
//     this.dsn = ''
//     this.database_name = -1
//   }
// }

// *****************************************************************************

@struct_like_registerer_decorator('AutomlDatabaseCredentials')
export class AutomlDatabaseCredentials extends AutomlBase {
  constructor() {
    super()
    this.version = '1.0.0'
    this.is_deleted = false
    this.username = ''
    this.password = ''
    this.dsn = ''
    this.database_name = -1
  }
}

// *****************************************************************************

const AutomlTaskStatusCategory = Object.freeze({
  queued: 0,
  running: 1,
  failed: 2,
  completed: 3,
  paused: 4,
  cancelled: 5,
})

export class AutomlTask extends StructLike {
  constructor() {
    super()
    this.uid = -1
    this.project_id = -1
    this.status = AutomlTaskStatusCategory.queued
  }
}

@struct_like_registerer_decorator('AutomlPipeInstantiationTask')
export class AutomlPipeInstantiationTask extends AutomlTask {
  constructor() {
    super()
    this.pipe_path = undefined
    this.poid = undefined
    this.task_category = undefined
  }
}

// *****************************************************************************
// *****************************************************************************
// *****************************************************************************

@struct_like_registerer_decorator('AutomlMetavariables')
export class AutomlMetavariables extends AutomlUniqueParentTable {
  constructor() {
    super()
    this.brick_name = 'metavariables'
    this.metavariables_formula = ''
  }
}

@struct_like_registerer_decorator('AutomlDummy')
export class AutomlDummy extends AutomlBrickBase {}

@struct_like_registerer_decorator('AutomlJoinTable')
export class AutomlJoinTable extends AutomlBrickBase {}

@struct_like_registerer_decorator('AutomlPivotTable')
export class AutomlPivotTable extends AutomlBrickBase {}

// *****************************************************************************
// *****************************************************************************
// *****************************************************************************

@struct_like_registerer_decorator('CsvFormatInfoType')
export class CsvFormatInfoType extends StructLike {
  constructor() {
    super()
    this.codec = 'utf_8'
    this.delimiter = ';'
    this.quotechar = '"'
    this.escapechar = '\\'
    this.doublequote = false
    this.skipinitialspace = false
    this.lineterminator = '\r\n'
    this.quoting = 0
  }
}

// *****************************************************************************

@struct_like_registerer_decorator('GenericColumnName')
export class GenericColumnName extends StructLike {
  constructor(name = '', contains_metavariables = false) {
    super()
    this.name = name
    this.contains_metavariables = contains_metavariables
    this.version = '1.0.0'
  }
  get_generic_column_name() {
    return this
  }
}

@struct_like_registerer_decorator('GenericColumnNameForCoercion')
export class GenericColumnNameForCoercion extends StructLike {
  constructor(
    name = '',
    contains_metavariables = false,
    type = 'string',
    date_formats = ['%Y-%m-%dT%H:%M:%S.%fZ']
  ) {
    super()
    this.generic_column_name = new GenericColumnName(
      name,
      contains_metavariables
    )
    this.type = type
    this.date_formats = date_formats
    this.version = '1.0.0'
  }
  get_generic_column_name() {
    return this.generic_column_name
  }
}

@struct_like_registerer_decorator('GenericColumnNameForUnicity')
export class GenericColumnNameForUnicity extends StructLike {
  constructor(name = '', contains_metavariables = false, operation = 'min') {
    super()
    this.generic_column_name = new GenericColumnName(
      name,
      contains_metavariables
    )
    this.operation = operation
    this.version = '1.0.0'
  }
  get_generic_column_name() {
    return this.generic_column_name
  }
}

@struct_like_registerer_decorator('GenericColumnNameForRenaming')
export class GenericColumnNameForRenaming extends StructLike {
  constructor(
    name = '',
    contains_metavariables = false,
    new_generic_column_name
  ) {
    super()
    this.generic_column_name = new GenericColumnName(
      name,
      contains_metavariables
    )
    this.new_generic_column_name = _.isUndefined(new_generic_column_name)
      ? new GenericColumnName(name)
      : new_generic_column_name
    this.version = '1.0.0'
  }
  get_generic_column_name() {
    return this.generic_column_name
  }
}

// *****************************************************************************

@struct_like_registerer_decorator('WarningsStorerPipeTrackingInfo')
export class WarningsStorerPipeTrackingInfo extends StructLike {
  constructor() {
    super()
    this.warnings = []
  }
}

@struct_like_registerer_decorator('CodeEvaluatorPipeTrackingInfo')
export class CodeEvaluatorPipeTrackingInfo extends WarningsStorerPipeTrackingInfo {
  constructor() {
    super()
    this.code_output = {}
  }
}

@struct_like_registerer_decorator(
  'BrickManualFeatureEngineeringPipeTrackingInfo'
)
export class BrickManualFeatureEngineeringPipeTrackingInfo extends CodeEvaluatorPipeTrackingInfo {}

@struct_like_registerer_decorator('FileCoercionPipeTrackingInfo')
export class FileCoercionPipeTrackingInfo extends StructLike {
  constructor() {
    super()
  }
}

@struct_like_registerer_decorator('DispatchTablePipeTrackingInfo')
export class DispatchTablePipeTrackingInfo extends StructLike {
  constructor() {
    super()
    this.parent_table_poid = -1
  }
}

@struct_like_registerer_decorator('BrickApiInputPipeTrackingInfo')
export class BrickApiInputPipeTrackingInfo extends StructLike {
  constructor() {
    super()
    this.json_schema_evaluation_results_poid = -1
  }
}

@struct_like_registerer_decorator('ApiTablePipeTrackingInfo')
export class ApiTablePipeTrackingInfo extends StructLike {
  constructor() {
    super()
  }
}

@struct_like_registerer_decorator('SingleCvSearcherIterationPipeTrackingInfo')
export class SingleCvSearcherIterationPipeTrackingInfo extends StructLike {
  constructor() {
    super()
    this.hyperparameters = []
    this.value = -666
  }
}

@struct_like_registerer_decorator('SingleCvSearcherPipeTrackingInfo')
export class SingleCvSearcherPipeTrackingInfo extends StructLike {
  constructor() {
    super()
    this.iterations = []
  }
}

@struct_like_registerer_decorator('FullCvSearcherIterationPipeTrackingInfo')
export class FullCvSearcherIterationPipeTrackingInfo extends StructLike {
  constructor() {
    super()
    this.hyperparameters = []
    this.values = []
  }
}

@struct_like_registerer_decorator('FullCvSearcherPipeTrackingInfo')
export class FullCvSearcherPipeTrackingInfo extends StructLike {
  constructor() {
    super()
    this.iterations = []
  }
}

@struct_like_registerer_decorator('ModelSearcherPipeTrackingInfo')
export class ModelSearcherPipeTrackingInfo extends StructLike {
  constructor() {
    super()
    this.single_cv = -1
    this.full_cv = -1
  }
}

@struct_like_registerer_decorator('ModelSearcherImportancePipeTrackingInfo')
export class ModelSearcherImportancePipeTrackingInfo extends StructLike {
  constructor() {
    super()
    this.single_cv = -1
    this.full_cv = -1
    this.feature_importance = {}
    this.boruta = {}
  }
}

// *****************************************************************************

@struct_like_registerer_decorator('CaruanaSearchIterationPipeTrackingInfo')
export class CaruanaSearchIterationPipeTrackingInfo extends StructLike {
  constructor() {
    super()
    this.model_name = ''
    this.hyperparameters = ''
    this.value = -1
  }
}

@struct_like_registerer_decorator('CaruanaSearchPipeTrackingInfo')
export class CaruanaSearchPipeTrackingInfo extends StructLike {
  constructor() {
    super()
    this.model_searches = {}
    this.caruana_iterations = []
  }
}

@struct_like_registerer_decorator('CaruanaStackerModelPipeTrackingInfo')
export class CaruanaStackerModelPipeTrackingInfo extends StructLike {
  constructor() {
    super()
    this.best_nb_iters = -1
    this.best_score = -1
    this.models = []
    this.weights = []
  }
}

@struct_like_registerer_decorator('CaruanaStackerPipeTrackingInfo')
export class CaruanaStackerPipeTrackingInfo extends StructLike {
  constructor() {
    super()
    this.caruana_search = {}
    this.model_stacking = {}
  }
}

// TODO: replace with CaruanaStackerPipeTrackingInfo /
// BrickGenericModelPipeTrackingInfo combination
@struct_like_registerer_decorator('CaruanaStackerImportancePipeTrackingInfo')
export class CaruanaStackerImportancePipeTrackingInfo extends StructLike {
  constructor() {
    super()
    this.caruana_search = {}
    this.model_stacking = {}
    this.feature_importance = {}
  }
}

@struct_like_registerer_decorator('BrickGenericModelPipeTrackingInfo')
export class BrickGenericModelPipeTrackingInfo extends StructLike {
  constructor() {
    super()
    this.search_poid = -1
    this.boruta_poid = -1
    this.probability_poid = -1
    this.roc_curve_poid = -1
  }
}

@struct_like_registerer_decorator('BorutaPipeTrackingInfo')
export class BorutaPipeTrackingInfo extends StructLike {
  constructor() {
    super()
    this.colnames = []
    this.imp_history = []
    this.total_imp_hits = []
    this.cols_status = []
    this.rejected_ranks = []
  }
}

// *****************************************************************************

export const CoercionFormatCategory = Object.freeze({
  unknown: 0,
  numeric_dtype: 1,
  str_limiter: 2,
  datetimes: 3,
})

export const CoercionFormatCategoryToString = _.invert(CoercionFormatCategory)

export class CoercionFormatBaseType {}

@struct_like_registerer_decorator('CoercionFormatUnknownType')
export class CoercionFormatUnknownType extends CoercionFormatBaseType {
  constructor() {
    super()
    this.category = CoercionFormatCategory.unknown
  }
}

export const CoercionFormatDtypeInfo = [
  ['float', 'dtype_float', 0],
  ['uint8', 'dtype_uint8', 1],
  ['uint16', 'dtype_uint16', 2],
  ['uint32', 'dtype_uint32', 3],
  ['uint64', 'dtype_uint64', 4],
  ['int8', 'dtype_int8', 5],
  ['int16', 'dtype_int16', 6],
  ['int32', 'dtype_int32', 7],
  ['int64', 'dtype_int64', 8],
]

export const CoercionFormatDtypeCategory = Object.freeze(
  _.reduce(
    CoercionFormatDtypeInfo,
    (prev, [name, identifier, value]) => {
      prev[identifier] = value
      return prev
    },
    {}
  )
)

@struct_like_registerer_decorator('CoercionFormatNumericDtypeType')
export class CoercionFormatNumericDtypeType extends CoercionFormatBaseType {
  constructor() {
    super()
    this.category = CoercionFormatCategory.numeric_dtype
    this.dtype = 0
    this.thousandseparator = ''
    this.decimalseparator = '.'
    this.endingneg = false
  }
}

@struct_like_registerer_decorator('CoercionFormatStrLimiterType')
export class CoercionFormatStrLimiterType extends CoercionFormatBaseType {
  constructor() {
    super()
    this.category = CoercionFormatCategory.str_limiter
    this.max_characters = 1024
  }
}

@struct_like_registerer_decorator('CoercionFormatDatetimesType')
export class CoercionFormatDatetimesType extends CoercionFormatBaseType {
  constructor() {
    super()
    this.category = CoercionFormatCategory.datetimes
    this.datetime_formats = []
  }
}

export class CoercionFormatVariantType {
  constructor() {
    this.category = CoercionFormatCategory.numeric_dtype
    this.numeric_dtype = new CoercionFormatNumericDtypeType()
    this.str_limiter = new CoercionFormatStrLimiterType()
    this.datetimes = new CoercionFormatDatetimesType()
  }
  static from_coercion_format(v) {
    let res = new CoercionFormatVariantType()
    const category = v.category
    res.category = category
    res[CoercionFormatCategoryToString[category]] = _.clone(v)
    return res
  }
  to_coercion_format() {
    const category = this.category
    let res = _.clone(this[CoercionFormatCategoryToString[category]])
    return res
  }
}

@struct_like_registerer_decorator('ColumnsetFormatInfoType')
export class ColumnsetFormatInfoType extends StructLike {
  constructor() {
    super()
    this.file_contains_headers = true
    this.coercers = []
  }
}

@struct_like_registerer_decorator('CoercionInfoDelta')
export class CoercionInfoDelta extends StructLike {
  constructor() {
    super()
    this.file_contains_headers = true
    this.columns_coercion_delta = []
    this.version = '1.0.0'
  }
}

@struct_like_registerer_decorator('ColumnstoreMetadata')
export class ColumnstoreMetadata extends StructLike {}

@struct_like_registerer_decorator('ColumnstoreColumnMetadata')
export class ColumnstoreColumnMetadata extends StructLike {}

@struct_like_registerer_decorator('ColumnstoreColumnGenericMetadata')
export class ColumnstoreColumnGenericMetadata extends StructLike {}

@struct_like_registerer_decorator('ColumnstoreColumnHistogramMetadata')
export class ColumnstoreColumnHistogramMetadata extends StructLike {}

@struct_like_registerer_decorator('DisplayFormatBase')
export class DisplayFormatBase extends StructLike {}

@struct_like_registerer_decorator('DisplayFormatFloat')
export class DisplayFormatFloat extends DisplayFormatBase {}

@struct_like_registerer_decorator('DisplayFormatDatetime')
export class DisplayFormatDatetime extends DisplayFormatBase {}

@struct_like_registerer_decorator('DisplayFormatString')
export class DisplayFormatString extends DisplayFormatBase {}

@struct_like_registerer_decorator('DisplayFormatBool')
export class DisplayFormatBool extends DisplayFormatBase {}

@struct_like_registerer_decorator('ColumnstoreColumnFilter')
export class ColumnstoreColumnFilter extends StructLike {
  constructor() {
    super()
    this.type = 'selected'
    this.values = []
  }
}

// *****************************************************************************

@struct_like_registerer_decorator('MetavariableTypeAbstractBase')
export class MetavariableTypeAbstractBase extends StructLike {
  static category = 'unknown'
  constructor() {
    super()
  }
}

@struct_like_registerer_decorator('MetavariableTypeEnumerationCategory')
export class MetavariableTypeEnumerationCategory extends MetavariableTypeAbstractBase {
  static category = 'enumeration'
  constructor(values = []) {
    super()
    this.values = values
  }
}

@struct_like_registerer_decorator('MetavariableTypeTemporalCategory')
export class MetavariableTypeTemporalCategory extends MetavariableTypeAbstractBase {
  static category = 'temporal'
  constructor() {
    super()
    this.rrulestr = ''
    this.display_format = ''
    this.use_previous_period = false
  }
}

@struct_like_registerer_decorator('TemporalMetavariableInstance')
export class TemporalMetavariableInstance extends StructLike {
  toJSON() {
    return this.v
  }
}

// *****************************************************************************

@struct_like_registerer_decorator('CriticalErrorsRegistryType')
export class CriticalErrorsRegistryType extends AutomlBase {
  constructor() {
    super()
    this.errors = {}
  }
}

@struct_like_registerer_decorator('AutomlPlatformStatus')
export class AutomlPlatformStatus extends AutomlBase {
  constructor() {
    super()
    this.uid = 0
    this.is_powered = true
    this.is_running = false
    this.current_running_project_id = -1
    this.current_running_brick_ids = new Collections.Set()
    this.current_running_task_id = -1
    this.current_running_poid = -1
    this.critical_errors = new CriticalErrorsRegistryType()
  }
}

export class ColumnsetFormatInfoVariantType {
  constructor() {
    this.file_contains_headers = true
    this.coercers = []
  }
  static from_columnset_format_info(v) {
    let res = new ColumnsetFormatInfoVariantType()
    _.assign(res, v)
    const c = v.coercers
    res.coercers = _.map(c, ([k, v]) => [
      k,
      CoercionFormatVariantType.from_coercion_format(v),
    ])
    return res
  }
  to_columnset_format_info() {
    let res = new ColumnsetFormatInfoType()
    _.assign(res, this)
    const c = this.coercers
    res.coercers = _.map(c, ([k, v]) => [k, v.to_coercion_format()])
    return res
  }
}

// *****************************************************************************

@struct_like_registerer_decorator('LicenseInformation')
export class LicenseInformation extends StructLike {
  constructor() {
    super()
    this.validity_timespans = []
    this.text_message = ''
    this.signature = ''
  }
}

// *****************************************************************************

@struct_like_registerer_decorator('BackendTranslatableException')
export class BackendTranslatableException extends StructLike {}

// *****************************************************************************

@struct_like_registerer_decorator('GenericPath')
export class GenericPath extends StructLike {
  constructor(path = '', contains_metavariables = false) {
    super()
    this.path = path
    this.contains_metavariables = contains_metavariables
    this.version = '1.0.0'
  }
}

// *****************************************************************************

@struct_like_registerer_decorator('AggregationBrickPipeTrackingInfo')
export class AggregationBrickPipeTrackingInfo extends WarningsStorerPipeTrackingInfo {
  constructor(
    automatic_relationships_poid = -1,
    corrected_relationships_poid = -1
  ) {
    super()
    this.automatic_relationships_poid = automatic_relationships_poid
    this.corrected_relationships_poid = corrected_relationships_poid
  }
}
